import {
  all, call, put, takeLatest,
} from 'redux-saga/effects'
import { actions } from './PendingReceivementsState'
import api from './PendingReceivementsApi'
import { actions as appActions } from '../state'
import { ReturnCall } from '../types'
import { toast } from 'react-toastify'
import {
  BadgeStatus, ApplyInvoicesManagementSagaParams, GetPendingReceivementPayload, GetReceivementConferenceParams, SendReceivementConferenceParams, ValidateInvoiceKeySagaParams,
  AssociateProductsReceivementPayload,
  ConfirmWrongProductPayload,
} from './PendingReceivementsTypes'
import { PayloadAction } from '@reduxjs/toolkit'

function* getPendingReceivements(fulfillmentCenterId: PayloadAction<GetPendingReceivementPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof api.getPendingReceivements> = yield call(
      api.getPendingReceivements,
      fulfillmentCenterId.payload,
    )

    if (!result.isOk) {
      throw new Error()
    } else if (!result.data) return

    yield put(actions.setPendingReceivements({ pendingReceivements: result.data }))
  } catch (err: any) {
    toast.error('Falha ao carregar recebimentos pendentes!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* getReceivementConference(params: PayloadAction<GetReceivementConferenceParams>) {
  yield put(actions.setLoading(true))

  try {
    const response: ReturnCall<typeof api.getReceivementConference> = yield call(
      api.getReceivementConference,
      params.payload,
    )

    if (!response.isOk) {
      toast.error(response.data?.data?.message || 'Recebimento não encontrado!')
      return
    }

    yield put(actions.setReceivementConference(response.data))
  } catch (error: any) {
    toast.error('Falha ao carregar recebimento para conferência!')
  } finally {
    yield put(actions.setLoading(false))
  }
}

export function* sendReceivementConference(params: PayloadAction<SendReceivementConferenceParams>) {
  yield put(actions.setReceivementConferenceLoading(true))

  const { history, setIsDisabled } = params.payload

  try {
    const response: ReturnCall<typeof api.sendReceivementConference> = yield call(
      api.sendReceivementConference,
      params.payload,
    )

    if (!response.isOk) {
      toast.error(response.data?.data?.message || 'Falha ao finalizar conferência')
      return
    }

    toast.success('Conferência finalizada com sucesso!')
    history.push('/pending-receivements')
  } catch (error: any) {
    toast.error('Falha ao finalizar conferência')
  } finally {
    yield put(actions.setReceivementConferenceLoading(false))
    setIsDisabled(false)
  }
}

export function* validateSupervisorBadge(params: PayloadAction<string>) {
  yield put(actions.setLoading(true))

  try {
    const response: ReturnCall<typeof api.validateSupervisorBadge> = yield call(
      api.validateSupervisorBadge,
      params.payload,
    )

    if (response.isOk) {
      yield put(actions.setBadge(params.payload))
      yield put(actions.setBadgeStatus(BadgeStatus.APROVED))
    } else {
      toast.error('Acesso negado!')
    }
  } catch (error: any) {
    toast.error('Acesso negado!')
  } finally {
    yield put(actions.setLoading(false))
  }
}

export function* validateInvoiceKey({ payload }: PayloadAction<ValidateInvoiceKeySagaParams>) {
  const {
    receivementId, invoiceKey, badge, onSuccess, setError, setIsDisabled,
  } = payload

  try {
    const response: ReturnCall<typeof api.validateInvoiceKey> = yield call(
      api.validateInvoiceKey,
      { receivementId, invoiceKey, badge },
    )

    if (!response.isOk) {
      setError(response.data?.data?.message || 'Falha ao validar chave da nota fiscal')
      return
    }

    onSuccess()
  } catch (error: any) {
    setError('Falha ao validar chave da nota fiscal')
  } finally {
    setIsDisabled(false)
  }
}

export function* applyInvoicesManagement({ payload }: PayloadAction<ApplyInvoicesManagementSagaParams>) {
  const {
    receivementId, invoiceKeys, badge, onSuccess, setIsDisabled,
  } = payload

  try {
    const response: ReturnCall<typeof api.applyInvoicesManagement> = yield call(
      api.applyInvoicesManagement,
      { receivementId, invoiceKeys, badge },
    )

    if (!response.isOk) {
      toast.error(response.data?.data?.message || 'Falha ao adicionar/remover notas fiscais')
      return
    }

    yield put(actions.setReceivementConference(response.data))

    onSuccess()
  } catch (error: any) {
    toast.error('Falha ao adicionar/remover notas fiscais')
  } finally {
    setIsDisabled(false)
  }
}

export function* associateProductsReceivementThirdConference(data: PayloadAction<AssociateProductsReceivementPayload>) {
  yield put(appActions.isLoading(true))
  const { onSuccess } = data.payload
  try {
    const response: ReturnCall<typeof api.associateProductsReceivementThirdConference> = yield call(
      api.associateProductsReceivementThirdConference,
      data.payload,
    )

    if (!response.isOk) {
      toast.error(response.data?.data?.message || 'Falha ao associar produto ao AR item!')
      return
    }

    yield put(actions.setReceivementConference(response.data))
    onSuccess()
  } catch (error: any) {
    toast.error('Falha ao associar produto ao AR item!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* confirmWrongProduct(data: PayloadAction<ConfirmWrongProductPayload>) {
  yield put(appActions.isLoading(true))
  const { onSuccess } = data.payload
  try {
    const response: ReturnCall<typeof api.confirmWrongProduct> = yield call(
      api.confirmWrongProduct,
      data.payload,
    )

    if (!response.isOk) {
      toast.error(response.data?.data?.message || 'Falha ao confirmar produto errado!')
      return
    }

    const productWrong = response.data

    yield put(actions.setConfirmWrongProduct(productWrong.idProductDivergence))
    onSuccess()
  } catch (error: any) {
    toast.error('Falha ao confirmar produto errado!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export default function* () {
  yield all([
    takeLatest(actions.getPendingReceivements.type, getPendingReceivements),
    takeLatest(actions.getReceivementConference.type, getReceivementConference),
    takeLatest(actions.validateSupervisorBadge.type, validateSupervisorBadge),
    takeLatest(actions.sendReceivementConference.type, sendReceivementConference),
    takeLatest(actions.validateInvoiceKey.type, validateInvoiceKey),
    takeLatest(actions.applyInvoicesManagement.type, applyInvoicesManagement),
    takeLatest(actions.associateProductsReceivementThirdConference.type, associateProductsReceivementThirdConference),
    takeLatest(actions.confirmWrongProduct.type, confirmWrongProduct),
  ])
}
